import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Phone, 
  MessageSquare, 
  AlertTriangle, 
  MapPin, 
  Clock, 
  Send,
  FileText,
  Shield,
  Navigation,
  Zap
} from 'lucide-react';

interface EmergencyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface EmergencyContact {
  name: string;
  number: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const EmergencyModal: React.FC<EmergencyModalProps> = ({ isOpen, onClose }) => {
  const [selectedOption, setSelectedOption] = useState<'main' | 'text' | 'report'>('main');
  const [customMessage, setCustomMessage] = useState('');
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const emergencyContacts: EmergencyContact[] = [
    {
      name: '911 Emergency',
      number: '911',
      description: 'Police, Fire, Medical Emergency',
      icon: <Phone className="w-6 h-6" />,
      color: 'bg-accent-red'
    },
    {
      name: 'Poison Control',
      number: '1-800-222-1222',
      description: '24/7 Poison Emergency Hotline',
      icon: <Shield className="w-6 h-6" />,
      color: 'bg-accent-orange'
    },
    {
      name: 'Crisis Text Line',
      number: '741741',
      description: 'Text HOME for mental health crisis',
      icon: <MessageSquare className="w-6 h-6" />,
      color: 'bg-primary-400'
    },
    {
      name: 'Disaster Hotline',
      number: '1-800-621-3362',
      description: 'FEMA Disaster Assistance',
      icon: <AlertTriangle className="w-6 h-6" />,
      color: 'bg-accent-yellow'
    }
  ];

  const quickMessages = [
    'I need immediate help',
    'Medical emergency',
    'Natural disaster assistance needed',
    'I am trapped and need rescue',
    'Fire emergency',
    'Severe weather emergency'
  ];

  useEffect(() => {
    if (isOpen) {
      // Request location when modal opens
      getCurrentLocation();
    }
  }, [isOpen]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      return;
    }

    setIsGettingLocation(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setIsGettingLocation(false);
      },
      (error) => {
        setLocationError('Unable to get your location. Please enable location services.');
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const handleCall = (number: string) => {
    if (typeof window !== 'undefined') {
      window.location.href = `tel:${number}`;
    }
  };

  const handleSendMessage = (message: string) => {
    const locationText = userLocation 
      ? `Location: ${userLocation.lat.toFixed(6)}, ${userLocation.lng.toFixed(6)}`
      : 'Location: Unable to determine';
    
    const fullMessage = `${message}\n\n${locationText}\n\nSent via DisasterConnect Emergency System`;
    
    // For SMS
    if (typeof window !== 'undefined') {
      window.location.href = `sms:911?body=${encodeURIComponent(fullMessage)}`;
    }
    
    console.log('Emergency message sent:', fullMessage);
  };

  const handleCustomMessage = () => {
    if (customMessage.trim()) {
      handleSendMessage(customMessage);
      setCustomMessage('');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            className="bg-dark-800 border border-dark-600 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-dark-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-accent-red rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Emergency Contact</h2>
                  <p className="text-sm text-gray-400">Get help when you need it most</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Warning Banner */}
            <div className="p-4 bg-accent-red/10 border-b border-accent-red/20">
              <div className="flex items-center space-x-2 text-accent-red">
                <Zap className="w-5 h-5" />
                <p className="font-semibold">For life-threatening emergencies, call 911 immediately</p>
              </div>
            </div>

            {/* Location Status */}
            <div className="p-4 border-b border-dark-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">Location Status</span>
                </div>
                {isGettingLocation ? (
                  <div className="flex items-center space-x-2 text-accent-orange">
                    <div className="w-3 h-3 border border-accent-orange border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs">Getting location...</span>
                  </div>
                ) : userLocation ? (
                  <div className="flex items-center space-x-2 text-accent-green">
                    <div className="w-2 h-2 bg-accent-green rounded-full" />
                    <span className="text-xs">Location available</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={getCurrentLocation}
                      className="text-xs text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      Enable Location
                    </button>
                  </div>
                )}
              </div>
              {locationError && (
                <p className="text-xs text-accent-orange mt-1">{locationError}</p>
              )}
            </div>

            {/* Content */}
            <div className="p-6">
              {selectedOption === 'main' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  {/* Emergency Contacts */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Phone className="w-5 h-5 text-accent-red" />
                      Emergency Services
                    </h3>
                    <div className="grid gap-3">
                      {emergencyContacts.map((contact, index) => (
                        <motion.button
                          key={index}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleCall(contact.number)}
                          className="flex items-center space-x-4 p-4 bg-dark-700/50 hover:bg-dark-700 border border-dark-600 rounded-lg transition-all duration-200"
                        >
                          <div className={`w-12 h-12 ${contact.color} rounded-lg flex items-center justify-center text-white`}>
                            {contact.icon}
                          </div>
                          <div className="flex-1 text-left">
                            <h4 className="font-semibold text-white">{contact.name}</h4>
                            <p className="text-sm text-gray-400">{contact.description}</p>
                            <p className="text-sm text-primary-400 font-mono">{contact.number}</p>
                          </div>
                          <Phone className="w-5 h-5 text-gray-400" />
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-primary-400" />
                      Quick Actions
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedOption('text')}
                        className="p-4 bg-primary-400/10 hover:bg-primary-400/20 border border-primary-400/30 rounded-lg transition-all duration-200 text-center"
                      >
                        <MessageSquare className="w-6 h-6 text-primary-400 mx-auto mb-2" />
                        <p className="text-sm font-medium text-white">Send Emergency Text</p>
                      </motion.button>
                      
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedOption('report')}
                        className="p-4 bg-accent-orange/10 hover:bg-accent-orange/20 border border-accent-orange/30 rounded-lg transition-all duration-200 text-center"
                      >
                        <FileText className="w-6 h-6 text-accent-orange mx-auto mb-2" />
                        <p className="text-sm font-medium text-white">Report Incident</p>
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              )}

              {selectedOption === 'text' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setSelectedOption('main')}
                      className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                    >
                      <Navigation className="w-4 h-4 rotate-180" />
                    </button>
                    <h3 className="text-lg font-semibold">Send Emergency Text</h3>
                  </div>

                  {/* Quick Message Templates */}
                  <div>
                    <h4 className="text-sm font-medium mb-3 text-gray-300">Quick Messages</h4>
                    <div className="space-y-2">
                      {quickMessages.map((message, index) => (
                        <motion.button
                          key={index}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => handleSendMessage(message)}
                          className="w-full p-3 text-left bg-dark-700/50 hover:bg-dark-700 border border-dark-600 rounded-lg transition-all duration-200"
                        >
                          <p className="text-sm text-white">{message}</p>
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Message */}
                  <div>
                    <h4 className="text-sm font-medium mb-3 text-gray-300">Custom Message</h4>
                    <div className="space-y-3">
                      <textarea
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        placeholder="Describe your emergency situation..."
                        className="w-full h-24 px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      />
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleCustomMessage}
                        disabled={!customMessage.trim()}
                        className="w-full bg-gradient-to-r from-primary-400 to-primary-500 py-3 rounded-lg font-semibold text-white hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <Send className="w-5 h-5" />
                        Send Emergency Text
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              )}

              {selectedOption === 'report' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setSelectedOption('main')}
                      className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                    >
                      <Navigation className="w-4 h-4 rotate-180" />
                    </button>
                    <h3 className="text-lg font-semibold">Report Non-Emergency Incident</h3>
                  </div>

                  <div className="bg-accent-yellow/10 border border-accent-yellow/30 rounded-lg p-4">
                    <div className="flex items-center space-x-2 text-accent-yellow mb-2">
                      <Clock className="w-4 h-4" />
                      <p className="text-sm font-medium">Non-Emergency Reporting</p>
                    </div>
                    <p className="text-xs text-gray-300">
                      Use this for situations that don't require immediate emergency response but should be documented.
                    </p>
                  </div>

                  <form className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Incident Type</label>
                      <select className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors">
                        <option value="">Select incident type</option>
                        <option value="property_damage">Property Damage</option>
                        <option value="power_outage">Power Outage</option>
                        <option value="water_issue">Water/Utility Issue</option>
                        <option value="road_hazard">Road Hazard</option>
                        <option value="environmental">Environmental Concern</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <textarea
                        placeholder="Describe the incident in detail..."
                        className="w-full h-24 px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Location</label>
                      <input
                        type="text"
                        placeholder="Enter specific location or address"
                        className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      className="w-full bg-gradient-to-r from-accent-orange to-accent-red py-3 rounded-lg font-semibold text-white hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                    >
                      <FileText className="w-5 h-5" />
                      Submit Report
                    </motion.button>
                  </form>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EmergencyModal;